import { ReactNode, createContext, useContext, useMemo } from "react";
import { useScheduleTemplates } from "@api/scheduleTemplate.api";
import { useTypedFlags } from "@hooks/useTypedFlags";
import {
    REVENUE_SCHEDULE_HUB_SCOPE,
    ScheduleHubScope,
    matchesScheduleHubScope,
    resolveScheduleHubScopes,
} from "./scheduleHubScope";

const ScheduleHubScopeContext = createContext<ScheduleHubScope | null>(null);

export const ScheduleHubScopeProvider = ({
    scope,
    children,
}: {
    scope: ScheduleHubScope;
    children: ReactNode;
}) => (
    <ScheduleHubScopeContext.Provider value={scope}>
        {children}
    </ScheduleHubScopeContext.Provider>
);

/** The hub page this surface sits in; null on surfaces outside a hub (bill, invoice, transaction drawers). */
export function useScheduleHubScope(): ScheduleHubScope | null {
    return useContext(ScheduleHubScopeContext);
}

/** The hub whose copy to show; unscoped surfaces keep the revenue wording. */
export function useScheduleHubCopy(): ScheduleHubScope {
    return useScheduleHubScope() ?? REVENUE_SCHEDULE_HUB_SCOPE;
}

export function useScheduleHubScopes(): ScheduleHubScope[] {
    const scope = useScheduleHubScope();
    const { schedulesExpenses: isExpensesAvailable } = useTypedFlags();
    return useMemo(
        () => resolveScheduleHubScopes({ scope, isExpensesAvailable }),
        [isExpensesAvailable, scope],
    );
}

export function useScheduleHubTemplates(
    options: Parameters<typeof useScheduleTemplates>[0],
) {
    const scope = useScheduleHubScope();
    const templatesQuery = useScheduleTemplates(options);
    const templates = useMemo(
        () =>
            templatesQuery.data?.filter((template) =>
                matchesScheduleHubScope(template, scope),
            ),
        [scope, templatesQuery.data],
    );
    return { data: templates, isError: templatesQuery.isError };
}
